import { Alert, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { supportLoginImpersonate, supportLoginGetDailyPassword, supportLoginGetStatus } from '../../../felles/APICalls_NET';
import { tokenRefreshService } from '../../../felles/TokenRefreshService';
import U from '../../../felles/Utils';
import * as actions from '../../../store/actions/index';
import LogoSimple from '../../components/SVGLogoSimple';
import { useStyles } from '../home/Home.styles';

/**
 * VitecPage — Vitec Software support login page (route: /vitec)
 *
 * Two-step flow:
 *  1. Click "Logg inn med Microsoft" → browser navigates to GET /login which triggers
 *     Entra OIDC (or the dev fake). On return the EntraSupportCookie is set.
 *  2. Enter an application username → POST /login/impersonate → receive JWT.
 *     The JWT is stored and the user is navigated into the app as that LDAP user.
 *
 * The URL parameter `?authed=1` is added by the redirect_uri we pass to the API,
 * and is used to distinguish step 1 (not yet authenticated) from step 2 (cookie set).
 */
export default function VitecPage() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dailyPassword, setDailyPassword] = useState('');
  const [dailyPasswordLoading, setDailyPasswordLoading] = useState(false);
  const [dailyPasswordError, setDailyPasswordError] = useState('');
  const [authCheck, setAuthCheck] = useState<'pending' | 'ok' | 'unauthorized' | 'forbidden'>('pending');

  // After the Entra/dev-signin redirect, the API appends ?authed=1 to the redirect_uri
  const params = new URLSearchParams(window.location.search);
  const isAuthenticated = params.get('authed') === '1';

  const handleEntraLogin = () => {
    const redirectBack =
      window.location.origin + U.HOMEPAGE_CONTEXT + '/vitec?authed=1';
    window.location.href =
      `${U.URL_ADDRESS}${U.WEBAPI_PATH}/login` +
      `?redirect_uri=${encodeURIComponent(redirectBack)}`;
  };

  // After the OIDC redirect lands on ?authed=1, immediately verify that the session
  // cookie is valid AND the user belongs to the required support group. Without this
  // check the impersonation form would appear for any Entra user, and they would only
  // discover they lack access when trying to use it.
  useEffect(() => {
    if (!isAuthenticated) {
      handleEntraLogin();
      return;
    }
    supportLoginGetStatus()
      .then(() => setAuthCheck('ok'))
      .catch((err: any) => {
        const status: number | undefined = err?.response?.status;
        setAuthCheck(status === 403 ? 'forbidden' : 'unauthorized');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetDailyPassword = async () => {
    setDailyPasswordLoading(true);
    setDailyPasswordError('');
    setDailyPassword('');
    try {
      const res = await supportLoginGetDailyPassword();
      setDailyPassword(res.data?.password ?? '');
    } catch {
      setDailyPasswordError('Kunne ikke hente daglig passord. Prøv igjen.');
    } finally {
      setDailyPasswordLoading(false);
    }
  };

  const handleImpersonate = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = username.trim();
    if (!target) return;

    setLoading(true);
    setError('');

    try {
      const btsg = '' + new Date().getTime();
      const res = await supportLoginImpersonate(target, btsg);
      const token: string | undefined = res.data?.token;

      if (token) {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('btsg', btsg);
        dispatch(actions.setTabId(btsg));
        dispatch(actions.updateAutodataUserJWT(token));
        dispatch(actions.setUserAuthenticatedAgainstAutodataAPI(true));
        dispatch(actions.updateAutodataUserAuth(true));
        dispatch(actions.setIsLogged(1));
        tokenRefreshService.start();
        navigate(U.HOMEPAGE_CONTEXT + '/menu');
      }
    } catch (err: any) {
      const status: number | undefined = err?.response?.status;
      const msg: string | undefined = err?.response?.data?.message;

      if (status === 404)
        setError(msg ?? `Bruker '${target}' ble ikke funnet i LDAP.`);
      else if (status === 401 || status === 403)
        setError('Ikke tilgang. Entra-sesjonen er ugyldig eller utløpt – last siden på nytt og logg inn igjen.');
      else
        setError('En uventet feil oppstod. Sjekk konsollen og prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.backgroundImg}>
      {!isAuthenticated ? (
        /* ── Step 1: Auto-redirect to Entra login ────────────────────────── */
        <div className={classes.root} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div className="form-login__logo">
            <LogoSimple width="150" />
          </div>
          <div className="form-login__h1 centered">Vitec Support</div>
          <CircularProgress sx={{ mt: 2 }} />
        </div>
      ) : authCheck === 'pending' ? (
        /* ── Checking authorization after OIDC redirect ──────────────────── */
        <div className={classes.root} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div className="form-login__logo">
            <LogoSimple width="150" />
          </div>
          <div className="form-login__h1 centered">Vitec Support</div>
          <CircularProgress sx={{ mt: 2 }} />
        </div>
      ) : authCheck === 'forbidden' ? (
        /* ── Not a member of the required security group ─────────────────── */
        <div className={classes.root} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div className="form-login__logo">
            <LogoSimple width="150" />
          </div>
          <div className="form-login__h1 centered">Vitec Support</div>
          <Alert severity="error" sx={{ mt: 2, maxWidth: 400, textAlign: 'center' }}>
            Du er ikke medlem av rett sikkerhetsgruppe og har ikke tilgang til støtteinnlogging.
            Kontakt IT for å få tilgang.
          </Alert>
        </div>
      ) : authCheck === 'unauthorized' ? (
        /* ── Session invalid or expired ──────────────────────────────────── */
        <div className={classes.root} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div className="form-login__logo">
            <LogoSimple width="150" />
          </div>
          <div className="form-login__h1 centered">Vitec Support</div>
          <Alert severity="error" sx={{ mt: 2, maxWidth: 400, textAlign: 'center' }}>
            Sesjonen er ugyldig eller utløpt. Last siden på nytt og logg inn igjen.
          </Alert>
        </div>
      ) : (
        /* ── Step 2: Enter username to impersonate ───────────────────────── */
        <form
          className={classes.root}
          noValidate
          autoComplete="off"
          onSubmit={handleImpersonate}
        >
          <div className="form-login__logo">
            <LogoSimple width="150" />
          </div>
          <div className="form-login__h1 centered">Vitec Support</div>
          <div className="form-login__h2 marginTopBottom15">Impersonering</div>

          <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
            Skriv inn brukernavn for å logge inn som den brukeren.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.85rem' }}>
              {error}
            </Alert>
          )}

          <div className="form-login__texfield">
            <TextField
              id="username-textfield"
              type="text"
              label="Brukernavn"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              autoFocus
              inputProps={{ tabIndex: 1 }}
            />
          </div>

          <div className="form-login__submit-button centered marginTopBottom15">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
              disabled={loading || !username.trim()}
              tabIndex={2}
            >
              {loading
                ? <CircularProgress size={22} color="inherit" />
                : 'Impersoner bruker'}
            </Button>
          </div>

          <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 16, paddingTop: 16, width: '100%' }}>
            <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', color: 'text.secondary' }}>
              Daglig passord
            </Typography>
            <div className="form-login__submit-button centered">
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                disableElevation
                disabled={dailyPasswordLoading}
                onClick={handleGetDailyPassword}
                tabIndex={3}
                sx={{ color: 'white' }}
              >
                {dailyPasswordLoading
                  ? <CircularProgress size={22} color="inherit" />
                  : 'Hent daglig passord'}
              </Button>
            </div>
            {dailyPasswordError && (
              <Alert severity="error" sx={{ mt: 1, fontSize: '0.85rem' }}>
                {dailyPasswordError}
              </Alert>
            )}
            {dailyPassword && (
              <Alert severity="success" sx={{ mt: 1, fontSize: '1rem', fontFamily: 'monospace', letterSpacing: 2 }}>
                {dailyPassword}
              </Alert>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
